/**
 * Lista pracowników (spec 01 §2). Wszystko liczone z realnych danych tenanta:
 * liczniki chipów, wartości dropdownów i status — pusta firma widzi uczciwy
 * stan pusty, nie przykładowy zespół [D19].
 *
 * Dropdowny filtrów są w makiecie atrapami; u nas działają [D15].
 */
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  Avatar,
  Button,
  Card,
  ChevronRight,
  EmptyState,
  Input,
  Select,
  Spinner,
  StatusPill,
  Users,
  cx,
} from '@k1p4/ui'
import { api, type EmployeeListItem } from '../../api/client'
import { useHasPermission, useHasRole } from '../../auth/AuthProvider'
import { InviteModal } from './InviteModal'
import { InviteImportModal } from './InviteImportModal'
import { StaffMigrationModal } from './StaffMigrationModal'
import { PendingInvitations } from './PendingInvitations'
import styles from './Employees.module.css'

type StatusChip = 'all' | 'aktywny' | 'onboarding' | 'ending' | 'missing_docs'

const CHIPS: readonly StatusChip[] = ['all', 'aktywny', 'onboarding', 'ending', 'missing_docs']

type PillTone = 'success' | 'warning' | 'danger' | 'pink' | 'neutral'

/**
 * Status pracownika to ZAWSZE jego enum — „Brak dokumentów" i „Kończąca się
 * umowa" to flagi pochodne [D6], które go uzupełniają, a nie zastępują.
 *
 * Makieta pokazuje w kolumnie jedną pigułkę, bo w jej danych zawsze prawdziwy
 * był tylko jeden warunek. Na realnych danych zasłanianie enumu znaczyło, że
 * aktywny pracownik z brakiem papierka przestawał być widocznie aktywny —
 * czyli tabela gubiła najważniejszą informację [odstępstwo D33].
 */
function statusOf(item: EmployeeListItem): { key: string; tone: PillTone } {
  if (item.status === 'aktywny') return { key: 'aktywny', tone: 'success' }
  if (item.status === 'onboarding') return { key: 'onboarding', tone: 'pink' }
  return { key: item.status, tone: 'neutral' }
}

/** Flagi pochodne dopisywane obok statusu — kolejność od najpilniejszej. */
function derivedFlags(item: EmployeeListItem): Array<{ key: string; tone: PillTone }> {
  const flags: Array<{ key: string; tone: PillTone }> = []
  if (item.endingContract) flags.push({ key: 'ending', tone: 'warning' })
  if (item.missingDocuments) flags.push({ key: 'missing_docs', tone: 'danger' })
  return flags
}

export function Employees() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const canInvite = useHasRole('hr_admin')
  const canMigrate = useHasPermission('Płace : Zatwierdzanie') && canInvite
  const [inviteOpen, setInviteOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [migrationOpen, setMigrationOpen] = useState(false)
  const [chip, setChip] = useState<StatusChip>('all')
  const [dept, setDept] = useState('')
  const [type, setType] = useState('')
  const [loc, setLoc] = useState('')
  const [manager, setManager] = useState('')
  const [search, setSearch] = useState('')

  const query = useMemo(() => {
    const params = new URLSearchParams()
    if (chip !== 'all') params.set('status', chip)
    if (dept) params.set('dept', dept)
    if (type) params.set('type', type)
    if (loc) params.set('loc', loc)
    if (manager) params.set('manager', manager)
    if (search.trim()) params.set('q', search.trim())
    return params.toString()
  }, [chip, dept, type, loc, manager, search])

  const employees = useQuery({
    queryKey: ['employees', query],
    queryFn: () => api.employees(query),
  })

  if (employees.isPending) return <Spinner label={t('common.loading')} />
  if (employees.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const { items, counts, filters, scope } = employees.data
  const anyFilterActive =
    chip !== 'all' || dept !== '' || type !== '' || loc !== '' || manager !== '' || search !== ''

  function clearFilters(): void {
    setChip('all')
    setDept('')
    setType('')
    setLoc('')
    setManager('')
    setSearch('')
  }

  return (
    <>
      <div className={styles.header}>
        <div>
          <p className={styles.summary}>
            {/* Trzy liczby, trzy odmiany — jeden klucz dawał „1 osób” (fala 9, grupa 93). */}
            {[
              t('employees.summaryPeople', { count: counts.all }),
              t('employees.summaryDepartments', { count: filters.departments.length }),
              t('employees.summaryLocations', { count: filters.locations.length }),
            ].join(' · ')}
          </p>
          {scope.teamOnly ? <p className={styles.teamNote}>{t('employees.teamOnly')}</p> : null}
        </div>
        {/* Zapraszanie jest uprawnieniem hr_admina — inne role nie widzą akcji. */}
        {canInvite ? (
          <div className={styles.headActions}>
            {/* Import zaproszeń z pliku (Etap 54, ADR-0060) obok pojedynczego zaproszenia. */}
            {/* Przeniesienie kadry z innego programu (Etap 55) — wymaga też zatwierdzania list płac. */}
            {canMigrate ? (
              <Button variant="secondary" onClick={() => setMigrationOpen(true)}>
                {t('staffMigration.action')}
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              {t('inviteImport.action')}
            </Button>
            <Button onClick={() => setInviteOpen(true)}>{t('employees.invite')}</Button>
          </div>
        ) : null}
      </div>

      <div className={styles.chips}>
        {CHIPS.map((key) => {
          const value =
            key === 'all'
              ? counts.all
              : key === 'missing_docs'
                ? counts.missingDocs
                : counts[key as 'aktywny' | 'onboarding' | 'ending']
          return (
            <button
              key={key}
              type="button"
              className={cx(styles.chip, chip === key && styles.chipActive)}
              aria-pressed={chip === key}
              onClick={() => setChip(key)}
            >
              {t(`employees.chips.${key}`)}
              <span className={styles.chipCount}>{value}</span>
            </button>
          )
        })}
      </div>

      <div className={styles.filters}>
        <span className={styles.filtersLabel}>{t('employees.filtersLabel')}</span>
        <Select
          variant="filter"
          aria-label={t('employees.filters.dept')}
          value={dept}
          onChange={(event) => setDept(event.target.value)}
        >
          <option value="">{t('employees.filters.dept')}</option>
          {filters.departments.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </Select>
        <Select
          variant="filter"
          aria-label={t('employees.filters.type')}
          value={type}
          onChange={(event) => setType(event.target.value)}
        >
          <option value="">{t('employees.filters.type')}</option>
          {filters.types.map((option) => (
            <option key={option} value={option}>
              {t(`contractType.${option}`)}
            </option>
          ))}
        </Select>
        <Select
          variant="filter"
          aria-label={t('employees.filters.loc')}
          value={loc}
          onChange={(event) => setLoc(event.target.value)}
        >
          <option value="">{t('employees.filters.loc')}</option>
          {filters.locations.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
        <Select
          variant="filter"
          aria-label={t('employees.filters.manager')}
          value={manager}
          onChange={(event) => setManager(event.target.value)}
        >
          <option value="">{t('employees.filters.manager')}</option>
          {filters.managers.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </Select>
        <Input
          className={styles.search}
          type="search"
          aria-label={t('employees.filters.search')}
          placeholder={t('employees.filters.search')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {anyFilterActive ? (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            {t('employees.filters.clear')}
          </Button>
        ) : null}
      </div>

      {items.length === 0 ? (
        <Card pad="lg">
          <EmptyState
            icon={<Users size={28} />}
            title={t(anyFilterActive ? 'employees.emptyFiltered' : 'employees.emptyTitle')}
            description={t(
              anyFilterActive ? 'employees.emptyFilteredBody' : 'employees.emptyBody',
            )}
            {...(anyFilterActive
              ? {
                  cta: (
                    <Button variant="secondary" onClick={clearFilters}>
                      {t('employees.filters.clear')}
                    </Button>
                  ),
                }
              : {})}
          />
        </Card>
      ) : (
        <Card pad="flush" className={styles.tableCard}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">{t('employees.columns.person')}</th>
                  <th scope="col">{t('employees.columns.dept')}</th>
                  <th scope="col">{t('employees.columns.type')}</th>
                  <th scope="col">{t('employees.columns.loc')}</th>
                  <th scope="col">{t('employees.columns.status')}</th>
                  <th scope="col">
                    <span className="visually-hidden" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const status = statusOf(item)
                  const fullName = `${item.firstName} ${item.lastName}`
                  return (
                    <tr
                      key={item.id}
                      className={styles.row}
                      onClick={() => navigate(`/app/employees/${item.id}`)}
                    >
                      <td>
                        <span className={styles.person}>
                          <Avatar name={fullName} size={38} />
                          <span>
                            {/*
                              Nazwisko jest ODSYŁACZEM, nie samym tekstem: klik
                              w wiersz to wygoda dla myszy, ale bez tego linku
                              profil był nieosiągalny z klawiatury, a to jedyne
                              wejście na niego z listy. Wygląd bez zmian —
                              odsyłacz dziedziczy kolor i nie ma podkreślenia.
                            */}
                            <Link
                              to={`/app/employees/${item.id}`}
                              className={styles.personName}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {fullName}
                            </Link>
                            <span className={styles.personRole}>{item.position ?? '—'}</span>
                          </span>
                        </span>
                      </td>
                      <td className={item.department ? undefined : styles.muted}>
                        {item.department ?? '—'}
                      </td>
                      <td className={item.employmentType ? undefined : styles.muted}>
                        {item.employmentType ? t(`contractType.${item.employmentType}`) : '—'}
                      </td>
                      <td className={item.location ? undefined : styles.muted}>
                        {item.location ?? '—'}
                      </td>
                      <td>
                        <span className={styles.statusCell}>
                          <StatusPill tone={status.tone} dot>
                            {t(`employeeStatus.${status.key}`)}
                          </StatusPill>
                          {derivedFlags(item).map((flag) => (
                            <StatusPill key={flag.key} tone={flag.tone}>
                              {t(`employeeStatus.${flag.key}`)}
                            </StatusPill>
                          ))}
                        </span>
                      </td>
                      <td className={styles.chevron}>
                        <ChevronRight size={18} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Zaproszenia oczekujące na użycie — tylko dla roli, która zaprasza (Fala 6, grupa 32). */}
      {canInvite ? <PendingInvitations /> : null}

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={() => employees.refetch()}
      />
      <InviteImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => void employees.refetch()}
      />
      <StaffMigrationModal
        open={migrationOpen}
        onClose={() => setMigrationOpen(false)}
        onChanged={() => void employees.refetch()}
      />
    </>
  )
}
